// src/views/adminView/contractsView/useClientAccounts.js
// Usuarios de los comercios, compartidos por todo el hub Empresas en UNA carga:
// el tab Cuentas (lista global), la ficha de cada empresa (sus usuarios) y la
// columna "Usuarios" de la cartera de Cumplimiento.
//
// Fuentes y merge:
//   1) users/clients → usuarios de comercios con contrato ACTIVO, con
//      `companyId` POPULADO ({_id, name}). Es la fuente autoritativa.
//   2) users/status/user (activos del tenant) → los KAM/client que todavía no
//      aparecen en users/clients (p. ej. sin contrato activo); ahí companyId
//      viene sin popular o null.
// Prioridad: gana el doc de users/clients. El orden inverso (el histórico del
// tab clientes de /admin/users) dejaba la columna Comercio en "—", el KPI de
// comercios subcontado y la búsqueda por comercio rota.
//
// El filtro por empresa es SIEMPRE client-side (por u.companyId?._id): el
// param `companyId` de users/clients figura como backlog del backend y nunca
// fue ejercitado desde el front, así que no se confía en él.
//
// Caché módulo-level con TTL (patrón useClientVolume30d) + invalidación con
// broadcast: tras crear/editar/bloquear/eliminar, invalidateClientAccounts()
// hace refetchear a TODOS los consumidores montados. Mientras llega la carga
// nueva se mantiene la lista anterior (sin flash de skeleton).
import { useCallback, useEffect, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import { getClientUsers, getDeliveryUsers } from "../../../api/user";
import { roleSegment } from "../usersModule/usersConfig";

const CACHE_TTL_MS = 5 * 60 * 1000;

let cached = null; // { promise: Promise<Array|null>, at: number, key: string }
const listeners = new Set();

const toDocs = (res) =>
  res?.docs || res?.data?.docs || (Array.isArray(res) ? res : []);

const fetchAccounts = async (logisticCompanyId) => {
  try {
    const [clientRes, tenantRes] = await Promise.all([
      getClientUsers(),
      getDeliveryUsers("active", undefined, logisticCompanyId),
    ]);
    const clientDocs = toDocs(clientRes).filter(Boolean);
    const merged = [...clientDocs];
    const seen = new Set(clientDocs.map((u) => u._id));
    for (const u of toDocs(tenantRes)) {
      if (!u || seen.has(u._id)) continue;
      if (roleSegment(u?.role?.name) !== "clientes") continue;
      merged.push(u);
      seen.add(u._id);
    }
    return merged;
  } catch {
    return null;
  }
};

// Invalida la caché y notifica a todos los hooks montados para que refetcheen.
// Llamar tras cualquier mutación de un usuario de comercio (alta, edición,
// bloqueo, borrado, marcas de vendedor/supervisor).
export function invalidateClientAccounts() {
  cached = null;
  listeners.forEach((l) => l());
}

// accounts: Array (lista mergeada) | null (error) | undefined (primera carga).
// listOf(companyId) / countOf(companyId): SIEMPRE client-side. Devuelven
// [] / 0 como cero real solo cuando la lista cargó; null si falló o carga.
export function useClientAccounts() {
  const { user } = useAuth();
  const logisticCompanyId =
    user?.logisticCompany?._id || user?.logisticCompanyId || null;
  const [accounts, setAccounts] = useState(undefined);
  // Contador de invalidaciones: re-dispara el efecto de carga sin perder la
  // lista anterior (los consumidores no ven flash de skeleton en un refetch).
  const [bump, setBump] = useState(0);

  useEffect(() => {
    const onInvalidate = () => setBump((v) => v + 1);
    listeners.add(onInvalidate);
    return () => listeners.delete(onInvalidate);
  }, []);

  useEffect(() => {
    if (!logisticCompanyId) return undefined;
    let alive = true;
    if (
      !cached ||
      cached.key !== logisticCompanyId ||
      Date.now() - cached.at > CACHE_TTL_MS
    ) {
      cached = {
        promise: fetchAccounts(logisticCompanyId),
        at: Date.now(),
        key: logisticCompanyId,
      };
    }
    const entry = cached;
    entry.promise.then((value) => {
      // Un error no se persiste: el próximo mount/invalidate reintenta. Solo
      // si ESTA entrada sigue vigente — un fetch viejo que falla después de un
      // invalidate no debe borrar la entrada nueva.
      if (value === null && cached === entry) cached = null;
      if (alive) setAccounts(value);
    });
    return () => {
      alive = false;
    };
  }, [logisticCompanyId, bump]);

  const loading = accounts === undefined;

  const listOf = useCallback(
    (companyId) => {
      if (!Array.isArray(accounts) || !companyId) return null;
      return accounts.filter((u) => u?.companyId?._id === companyId);
    },
    [accounts],
  );

  const countOf = useCallback(
    (companyId) => {
      const list = listOf(companyId);
      return list === null ? null : list.length;
    },
    [listOf],
  );

  return { accounts, loading, listOf, countOf };
}

export default useClientAccounts;
